import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import { motion, AnimatePresence } from "framer-motion";
import Hero from "../components/Hero";
import SEO from "../components/SEO";
import API from "../api";
import { createCourseSchema } from "../utils/jsonLdSchemas";

const CoursesPage = () => {
    const [activeTab, setActiveTab] = useState(null);
    const [schools, setSchools] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    /** Fetch all schools */
    const fetchSchools = useCallback(async () => {
        try {
            const res = await API.get("/schools");
            const data =
                res.data && Array.isArray(res.data)
                    ? res.data
                    : res.data.schools || res.data.data || [];

            console.log(data);
            setSchools(data);

            // Default: select first school
            if (data.length > 0 && !activeTab) setActiveTab(data[0].id);
        } catch (err) {
            console.error("Failed to fetch schools", err);
            setError("Failed to load schools");
        }
    }, [activeTab]);

    /** Fetch all courses */
    const fetchCourses = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get("/courses");
            const data =
                res.data && Array.isArray(res.data)
                    ? res.data
                    : res.data.courses || res.data.data || [];
            setCourses(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch courses", err);
            setError(err.message || "Failed to load courses");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSchools();
        fetchCourses();
    }, [fetchSchools, fetchCourses]);

    /** Filter courses by school */
    const filteredCourses = courses.filter(
        (course) => course.school_id === activeTab
    );

    /** Find active school for Hero section */
    const activeSchool = schools.find((s) => s.id === activeTab);

    /** Dynamic Hero content */
    const heroSlides = activeSchool
        ? [
            {
                id: activeSchool.id,
                image: activeSchool.image_url || "/assets/img/hero-1.jpg",
                title: activeSchool.name,
                subtitle: "Explore a wide range of courses offered by our diverse schools.",
                buttonText: "View Courses",
                buttonLink: "#courses-section",
            },
        ]
        : [
            {
                id: "default",
                image: "/assets/img/prog-1.jpg",
                title: "Our Schools",
                subtitle:
                    "Explore a wide range of courses offered by our diverse schools.",
                buttonText: "Discover More",
                buttonLink: "#",
            },
        ];

    return (
        <section className="bg-gray-50 min-h-screen pt-1">
            {/* Create JSON-LD for courses list */}
            {courses.length > 0 && (
                <SEO 
                    title="Courses — Africa Research University" 
                    description="Browse ARU programmes and courses across our schools."
                    url="/courses"
                    jsonLd={{
                        '@context': 'https://schema.org',
                        '@type': 'CollectionPage',
                        'name': 'Courses',
                        'url': 'https://africaresearchuniversity.com/courses',
                        'description': 'Browse ARU programmes and courses',
                        'hasPart': courses.slice(0, 10).map(course => createCourseSchema({
                            title: course.title,
                            description: course.description || course.introduction || '',
                            image: course.image,
                            url: `https://africaresearchuniversity.com/course/${course.id}`
                        }))
                    }}
                    breadcrumbs={[
                        { name: 'Home', url: 'https://africaresearchuniversity.com' },
                        { name: 'Courses', url: 'https://africaresearchuniversity.com/courses' }
                    ]}
                />
            )}
            {!courses.length && (
                <SEO 
                    title="Courses — Africa Research University" 
                    description="Browse ARU programmes and courses across our schools."
                    url="/courses"
                    breadcrumbs={[
                        { name: 'Home', url: 'https://africaresearchuniversity.com' },
                        { name: 'Courses', url: 'https://africaresearchuniversity.com/courses' }
                    ]}
                />
            )}
            {/* Hero Section (Dynamic) */}
            <Hero
                slides={heroSlides}
                interval={5000}
                height="350px"
                borderRadius="50px"
                hidden={true}
            />

            <div className="container mx-auto px-6 py-16" id="courses-section">
                {/* Tabs (Dynamic from API) */}
                <div className="flex flex-wrap justify-center gap-3 mb-10">
                    {schools.length > 0 ? (
                        schools.map((school) => (
                            <button
                                key={school.id}
                                onClick={() => setActiveTab(school.id)}
                                className={`px-5 py-2 rounded-full font-medium transition-all border ${activeTab === school.id
                                    ? "bg-green-600 text-white border-green-600 shadow-md"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                                    }`}
                            >
                                {school.name}
                            </button>
                        ))
                    ) : (
                        <div className="text-gray-500">Loading schools...</div>
                    )}
                </div>

                {/* Course List */}
                <AnimatePresence mode="wait">
                    {loading ? (
                        <div className="text-center text-gray-600">Loading courses...</div>
                    ) : error ? (
                        <div className="text-center text-red-600">{error}</div>
                    ) : (
                        <motion.ul
                            key={activeTab}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35 }}
                            className="space-y-4 max-w-4xl mx-auto"
                        >
                            {filteredCourses.length > 0 ? (
                                filteredCourses.map((course, i) => (
                                    <motion.li
                                        key={course.id}
                                        className="bg-white rounded-lg border hover:shadow-md transition overflow-hidden"
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.04 }}
                                        data-aos="fade-up"
                                        data-aos-delay={(i + 1) * 40}
                                    >
                                        <Link
                                            to={`/course/${course.id}`}
                                            className="flex items-center gap-3 w-full p-3 sm:p-4 text-current no-underline hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-green-200 rounded"
                                        >
                                            <span className="inline-block bg-green-50 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                                                {course.category || "Academic Program"}
                                            </span>
                                            <h3 className="text-md sm:text-lg font-semibold text-gray-900 truncate">
                                                {course.title || "Untitled Course"}
                                            </h3>
                                        </Link>
                                    </motion.li>
                                ))
                            ) : (
                                <div className="text-center text-gray-500">
                                    No courses available for this school.
                                </div>
                            )}
                        </motion.ul>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default CoursesPage;
